import React from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View, ViewProps } from "react-native";
import { useSelector } from "react-redux";
import { useNavigation } from "@react-navigation/native";
import moment from "moment";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useIsMobile } from "src/hooks/ui";

import { PdStack, PdText } from "src/components/layout";
import { PdFormLabel } from "src/components/form-control";
import { PdDateRangeInput } from "src/components/forms/input";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { formatMoney, formatPercentage } from "src/util/gFormatting";
import { useFocusableQuery } from "src/hooks/api";
import { getBrandStats, getBrandStatsMonthlySales, getBrandStatsSalesSnapshot } from "src/util/apiNew";

import { SLImage } from "../../components";
import { kRoute } from "../../kRoute";
import { PdCard } from "./card";
import { SalesLineChart } from "./charts";
import RecentOrdersTable from "./RecentOrdersTable";

const MonthlySalesCard: React.FC<ViewProps> = ({ style }) => {
  const isMobile = useIsMobile();
  const [period, setPeriod] = React.useState([moment().subtract(6, "month").toDate(), moment().toDate()]);
  const query = useFocusableQuery(
    React.useCallback(
      () =>
        getBrandStatsMonthlySales({
          start: moment(period[0]).startOf("month").format("YYYY-MM"),
          end: moment(period[1]).endOf("month").format("YYYY-MM"),
        }),
      [period],
    ),
  );

  const onChange = (dates: [Date, Date]) => {
    if (Array.isArray(dates) && dates[0] && dates[1]) {
      setPeriod(dates);
    }
  };

  return (
    <PdCard style={[styles.zIndex1, style]}>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        style={[styles.zIndex1, styles.mb4, isMobile ? [] : [styles.alignCenter, styles.justifyBetween]]}
      >
        <PdText style={[styles.fontMedium, styles.textLg]}>Monthly Sales</PdText>
        <View style={[styles.flexRow, styles.alignCenter]}>
          <PdFormLabel style={styles.mr3}>Date:</PdFormLabel>
          <PdDateRangeInput
            containerStyle={isMobile ? styles.flex1 : styles.widthMd}
            style={styles.textCenter}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            period={[period[0], period[1]]}
            startDate={period[0]}
            endDate={period[1]}
            onChange={onChange}
          />
        </View>
      </PdStack>
      <SalesLineChart data={query.data} />
    </PdCard>
  );
};

const SalesSnapshotCard: React.FC<ViewProps> = ({ style }) => {
  const isMobile = useIsMobile();
  const [period, setPeriod] = React.useState([moment().subtract(1, "month").toDate(), moment().toDate()]);
  const query = useFocusableQuery(
    React.useCallback(
      () =>
        getBrandStatsSalesSnapshot({
          start: moment(period[0]).format("YYYY-MM-DD"),
          end: moment(period[1]).format("YYYY-MM-DD"),
        }),
      [period],
    ),
  );

  const onChange = (dates: [Date, Date]) => {
    if (Array.isArray(dates) && dates[0] && dates[1]) setPeriod(dates);
  };

  return (
    <PdCard variant="solid" style={style}>
      <View style={[styles.flexRow, styles.alignCenter, styles.mlAuto, styles.mb4, styles.zIndex1]}>
        <PdFormLabel style={styles.mr3}>Date:</PdFormLabel>
        <PdDateRangeInput
          containerStyle={styles.flex1}
          style={[styles.textCenter, styles.textWhite]}
          dateFormat="MM/dd/yyyy"
          period={[period[0], period[1]]}
          startDate={period[0]}
          endDate={period[1]}
          onChange={onChange}
        />
      </View>
      <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite, styles.mb4]}>Sales Snapshot</PdText>
      <PdStack gap={4} layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"} style={styles.mb4}>
        <View>
          <PdFormLabel>Total Sales</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
            {formatMoney(query.data?.totalSales ?? 0)}
          </PdText>
          <PdText style={[styles.fontMedium, styles.textSm, styles.textWhite]}>{`${
            query.data?.totalSalesOrders ?? 0
          } ORDERS`}</PdText>
        </View>
        <View>
          <PdFormLabel>Promotional Sales</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
            {formatMoney(query.data?.promotionalSales ?? 0)}
          </PdText>
          <PdText style={[styles.fontMedium, styles.textSm, styles.textWhite]}>{`${
            query.data?.promotionalSalesOrders ?? 0
          } ORDERS`}</PdText>
        </View>
      </PdStack>

      <PdStack gap={4} layout={[1, 1]} direction={isMobile ? "vertical" : "horizontal"}>
        <View>
          <PdFormLabel>Conversion Rate</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
            {formatPercentage(query.data?.conversionRate ?? 0)}
          </PdText>
          <PdText style={[styles.fontMedium, styles.textXs, styles.textWhite]}>
            Calculation based on active retailer connections.
          </PdText>
        </View>
        {/* <View>
          <PdFormLabel>Unique views</PdFormLabel>
          <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{query.data?.uniqueVisits ?? 0}</PdText>
        </View> */}
      </PdStack>
    </PdCard>
  );
};

const BrandDashboard: React.FC = () => {
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const query = useFocusableQuery(getBrandStats);
  const rCompany = useSelector(({ CompanyReducer }) => CompanyReducer);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);

  const handleOnCreateOrder = () => {
    if (rCompany.type === "brand") navigation.navigate(kRoute.P100, { brand: rCompany.name });
  };

  const handleOnViewAbandonedCarts = () => {
    navigation.navigate(kRoute.P03_03);
  };

  const handleOnViewSalesReps = () => {
    navigation.navigate(kRoute.P12);
  };

  return (
    <ScrollView contentContainerStyle={isMobile ? styles.p5 : styles.p10}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[2, 1]} gap={4} style={styles.mb4}>
        <MonthlySalesCard />
        <SalesSnapshotCard />
      </PdStack>

      <PdCard style={styles.mb4}>
        <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Recent Orders</PdText>
        <RecentOrdersTable type="brand" companyId={rCompanyId} />
      </PdCard>

      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1, 1]} gap={4} style={styles.mb4}>
        <View style={[styles.border1, styles.borderGray, styles.px7, styles.py3]}>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Created Orders</PdText>
          <View style={styles.mb4}>
            <PdFormLabel>Total Sales</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.createdOrdersTotalSalesAmount ?? 0)}
            </PdText>
          </View>
          <View style={styles.mb4}>
            <PdFormLabel>Conversion Rate</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatPercentage(query.data?.createdOrdersConversionRate ?? 0)}
            </PdText>
          </View>
          <PdButton onPress={handleOnCreateOrder} style={styles.mrAuto}>
            <PdButtonLabel>Create an Order</PdButtonLabel>
          </PdButton>
        </View>

        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Abandoned Carts</PdText>
          <View style={styles.mb4}>
            <PdFormLabel>Total Abandoned Carts</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>{query.data?.abandonedCartsQuantity ?? 0}</PdText>
          </View>
          <View style={styles.mb4}>
            <PdFormLabel>Total Abandoned Carts Value</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.abandonedCartsTotalAmount ?? 0)}
            </PdText>
          </View>
          <PdButton onPress={handleOnViewAbandonedCarts} style={styles.mrAuto}>
            <PdButtonLabel>View Abandoned Carts</PdButtonLabel>
          </PdButton>
        </PdCard>

        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Sales Reps</PdText>
          <View style={styles.mb4}>
            <PdFormLabel>Total Sales Reps Sales</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.salesRepsTotalSalesAmount ?? 0)}
            </PdText>
          </View>
          <View style={styles.mb4}>
            <PdFormLabel>Total Sales Reps Written Orders</PdFormLabel>
            <PdText style={[styles.fontMedium, styles.textLg]}>
              {formatMoney(query.data?.salesRepsTotalWrittenOrdersAmount ?? 0)}
            </PdText>
          </View>
          <PdButton onPress={handleOnViewSalesReps} style={styles.mrAuto}>
            <PdButtonLabel>View Sales Reps</PdButtonLabel>
          </PdButton>
        </PdCard>
      </PdStack>

      {(query.data?.bestSellersProducts?.length ?? 0) > 0 && (
        <PdCard>
          <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>Best Sellers</PdText>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} gap={4} style={styles.justifyBetween}>
            {query.data?.bestSellersProducts.map((product, index) => (
              <TouchableOpacity
                key={product.id}
                onPress={() =>
                  rCompany.type === "brand" &&
                  navigation.navigate(kRoute.P101, { brand: rCompany.name, productId: product.id })
                }
                style={[styles.alignCenter, isMobile ? [] : styles.flex1]}
              >
                <SLImage
                  mobileStyle={styles.placeholderPortrait}
                  webStyle={webStyles.placeholderPortrait}
                  source={{ uri: product.imageUrl }}
                />
                <PdFormLabel style={[styles.textCenter, styles.mt1]}>{`#${index + 1}`}</PdFormLabel>
                <PdText style={[styles.textCenter, styles.mt1]}>{product.name}</PdText>
              </TouchableOpacity>
            ))}
          </PdStack>
        </PdCard>
      )}
    </ScrollView>
  );
};

export default BrandDashboard;

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  border1: {
    borderWidth: 1,
  },
  borderGray: {
    borderColor: kColors.gray300,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  fontMedium: {
    fontWeight: "400",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt1: {
    marginTop: 1 * kStyleConst.kSpacing,
  },
  p10: {
    padding: kStyleConst.kSpacing * 10,
  },
  p5: {
    padding: kStyleConst.kSpacing * 5,
  },
  placeholderPortrait: {
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    height: 80,
    width: 80,
  },
  px7: {
    paddingHorizontal: kStyleConst.kSpacing * 7,
  },
  py3: {
    paddingVertical: kStyleConst.kSpacing * 5,
  },
  textCenter: {
    textAlign: "center",
  },
  textLg: {
    fontSize: 22,
  },
  textSm: {
    fontSize: 12,
  },
  textWhite: {
    color: kColors.white,
  },
  textXs: {
    fontSize: 10,
  },
  widthMd: {
    width: 190,
  },
  zIndex1: {
    zIndex: 1,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  P02_ImageBackground: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: -1,
  },
  placeholderPortrait: {
    height: 80,
    width: 80,
    backgroundColor: kColors.white,
    borderColor: kColors.lightMed,
    borderWidth: 1,
  },
} as const;
